import React, { Fragment, useEffect } from 'react';
import { Tab, useTranslate, useShowController } from 'react-admin';
import Avatar from '@material-ui/core/Avatar';
import PricingIcon from '@material-ui/icons/AttachMoney';

import { Show, PaperTabbedShowLayout } from '~/layout';
import { Aside, PerFeature, PerFees, PerLimits, PerLoyalty, Settings } from './components';
import { Pricing, Solution } from '~/types';
import { getPricingSchema, PricingSchema } from '~/api/pricings';
import { useApiLazy, useSolution } from '~/hooks';
import { useProductById } from '~/hooks/product';
import { UpdateContext } from '~/components';
import { DraftDiffProvider } from '~/hooks/useDraftDiffContext';
import { useChangelogRecord } from '~/hooks/useChangelogContext';

export const l = (key: string): string => `resources.pricings.${key}`;

export const hasLimitsTab = (record?: Pricing): boolean => !!record?.productId;
export const hasLoyaltyTab = (solution?: Solution): boolean =>
  !!solution?.features?.includes('veengu_individual_loyalty');

const PricingShow = (props: any) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const showController = useShowController(props);
  const showRecord: Pricing = useChangelogRecord(showController.record);
  const controllerProps = { ...showController, record: showRecord };

  const { data: solution } = useSolution(showRecord?.solutionId);

  // Owns the product query for the whole page: this observer keeps the default
  // staleTime, so entering the pricing refetches it, while the tabs reuse the cache
  useProductById(showRecord?.productId);

  // Get pricing schema
  const [schemaQuery, { data: pricingSchema, loading }] = useApiLazy<PricingSchema>(
    getPricingSchema
  );
  useEffect(() => {
    if (showRecord?.productId) {
      schemaQuery({ productId: showRecord.productId, pricingFormat: showRecord.format });
    } else if (showRecord?.externalSourceType) {
      schemaQuery({ externalSourceType: showRecord.externalSourceType });
    }
  }, [schemaQuery, showRecord]);

  return (
    <Show
      icon={
        <Avatar>
          <PricingIcon />
        </Avatar>
      }
      {...props}
    >
      {showRecord && <UpdateContext data={{ solutionId: showRecord.solutionId }} />}
      <PaperTabbedShowLayout>
        <Tab label={t('tabs.settings')}>
          <DraftDiffProvider draft={showRecord}>
            <Settings {...controllerProps} />
          </DraftDiffProvider>
        </Tab>
        <Tab label={t('tabs.perFeatures')} path='perFeatures'>
          <DraftDiffProvider draft={showRecord}>
            <PerFeature {...controllerProps} pricingSchema={pricingSchema} />
          </DraftDiffProvider>
        </Tab>
        <Tab label={t('tabs.fees')} path='fees'>
          <DraftDiffProvider draft={showRecord}>
            <PerFees
              record={showRecord}
              loading={loading || showController.loading}
              pricingSchema={pricingSchema}
            />
          </DraftDiffProvider>
        </Tab>
        <Tab label={t('tabs.taxes')} path='taxes'>
          <DraftDiffProvider draft={showRecord}>
            <PerFees
              record={showRecord}
              loading={loading || showController.loading}
              pricingSchema={pricingSchema}
              tax
            />
          </DraftDiffProvider>
        </Tab>
        {hasLimitsTab(showRecord) && (
          <Tab label={t('tabs.limits')} path='limits'>
            <DraftDiffProvider draft={showRecord}>
              <PerLimits
                record={showRecord}
                loading={loading || showController.loading}
                pricingSchema={pricingSchema}
              />
            </DraftDiffProvider>
          </Tab>
        )}
        {hasLoyaltyTab(solution) && (
          <Tab label={t('tabs.loyalty')} path='loyalty'>
            <DraftDiffProvider draft={showRecord}>
              <PerLoyalty
                record={showRecord}
                loading={loading || showController.loading}
                pricingSchema={pricingSchema}
              />
            </DraftDiffProvider>
          </Tab>
        )}
      </PaperTabbedShowLayout>
      <Fragment>
        <div>
          <Aside {...controllerProps} />
        </div>
      </Fragment>
    </Show>
  );
};

export default PricingShow;
